import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight, Sparkles, Smartphone, Zap, RefreshCw, ShieldCheck,
  Truck, Star, Leaf, Gem, CreditCard, BarChart3, Calendar, Check,
} from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { PRODUCTS, productBySlug, formatMoney, getProductBasePrice } from "@/lib/ecom/catalog";
import { useCurrency } from "@/lib/ecom/currency";
import { alternateLinkList } from "@/lib/i18n/locale";
import metalHero from "@/assets/products/custom-black-metal.webp";
import bambooHero from "@/assets/products/bamboo-hero.webp";
import whiteHero from "@/assets/products/white-hero.webp";
import blackHero from "@/assets/products/custom-black-pvc.webp";
import goldHero from "@/assets/products/gold-hero.webp";
import googleHero from "@/assets/products/google-review-hero.webp";

const HERO_OG = metalHero;

export const Route = createFileRoute("/vi/shop")({
  head: () => ({
    meta: [
      { title: "Mua thẻ kinh doanh NFC AI — Kim loại, Tre, Vàng & PVC | banana.cards" },
      { name: "description", content: "Bộ sưu tập Banana Cards. Thẻ kinh doanh NFC cao cấp bằng kim loại, tre, vàng 24kt và PVC — cộng thẻ Google Review. Một chạm chia sẻ tất cả, AI capture mọi liên hệ." },
      { property: "og:title", content: "Mua bộ sưu tập Banana Cards" },
      { property: "og:description", content: "Thẻ kinh doanh NFC AI cao cấp. Chọn chất liệu — tap để chia sẻ, không bao giờ mất contact." },
      { property: "og:type", content: "website" },
      { property: "og:image", content: HERO_OG },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/shop", "vi"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "Store",
          name: "Banana Cards Shop",
          url: "https://banana.cards/vi/shop",
          description: "Thẻ kinh doanh NFC AI cao cấp và thẻ Google Review.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org/",
          "@type": "ItemList",
          itemListElement: PRODUCTS.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://banana.cards/vi${p.href}`,
            name: p.name,
          })),
        }),
      },
    ],
  }),
  component: ShopIndex,
});

type CardCopy = {
  slug: string;
  image: string;
  tagline: string;
  blurb: string;
  badge?: string;
  accent: string;
  bullets: string[];
  Icon: typeof Sparkles;
};

const COPY: CardCopy[] = [
  {
    slug: "metal",
    image: metalHero,
    tagline: "Thép không gỉ sơn tĩnh điện đen",
    blurb: "Thiết kế cho ấn tượng. Logo khắc laser, NFC nhúng, bảo hành trọn đời.",
    badge: "Bán chạy",
    accent: "bg-foreground text-background",
    bullets: ["Lõi thép không gỉ", "Logo khắc laser", "Bảo hành trọn đời"],
    Icon: Gem,
  },
  {
    slug: "24kt-gold",
    image: goldHero,
    tagline: "Mạ vàng 24kt, định hình thương hiệu",
    blurb: "Thẻ flex. Mạ vàng 24kt thật với khắc tùy chỉnh và AI follow-up.",
    badge: "Cao cấp",
    accent: "bg-[oklch(0.82_0.16_85)] text-black",
    bullets: ["Mạ vàng 24kt", "Hoàn thiện thủ công", "Onboarding concierge"],
    Icon: Sparkles,
  },
  {
    slug: "bamboo",
    image: bambooHero,
    tagline: "Tre nguồn bền vững",
    blurb: "Lựa chọn chu đáo. Ấm áp, tự nhiên và 100% phân hủy sinh học — không mất tính năng.",
    badge: "Eco",
    accent: "bg-brand-lime text-brand-lime-foreground",
    bullets: ["Tre bền vững", "Khắc thủ công", "Chương trình trồng cây"],
    Icon: Leaf,
  },
  {
    slug: "white-pvc",
    image: whiteHero,
    tagline: "PVC trắng bóng, ưu tiên thương hiệu",
    blurb: "Canvas sạch. In màu đầy đủ, logo to và sáng — giá thân thiện startup.",
    badge: "Giá trị nhất",
    accent: "bg-background text-foreground border border-foreground/15",
    bullets: ["In màu đầy đủ", "Nhẹ", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "black-pvc",
    image: blackHero,
    tagline: "PVC đen mờ, sleek & hiện đại",
    blurb: "Lựa chọn tàng hình. Hoàn thiện đen sâu với in sắc nét — flex tự tin, kín đáo.",
    badge: "Bán chạy",
    accent: "bg-foreground text-background",
    bullets: ["Hoàn thiện đen mờ", "In màu đầy đủ", "Apple & Google Wallet"],
    Icon: CreditCard,
  },
  {
    slug: "google-review",
    image: googleHero,
    tagline: "Review 5 sao theo yêu cầu",
    blurb: "Thẻ tăng trưởng. Tap trên quầy, khách để lại review — đơn giản vậy.",
    badge: "Cho local business",
    accent: "bg-blue-500 text-white",
    bullets: ["Trực tiếp tới review Google", "Không cần app", "Theo dõi mọi scan"],
    Icon: Star,
  },
];

function ShopIndex() {
  const { country } = useCurrency();
  return (
    <SiteLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-background">
        <div className="pointer-events-none absolute inset-0 -z-10 opacity-60">
          <div className="absolute -top-24 left-1/2 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-brand-lime/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-4 pb-12 pt-16 md:px-6 md:pb-16 md:pt-24">
          <div className="flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              <Sparkles className="h-3 w-3" /> Bộ sưu tập
            </span>
            <h1 className="mt-5 max-w-4xl font-display text-4xl leading-[1.05] tracking-tight md:text-6xl">
              Chọn thẻ của bạn.<br className="hidden md:block" />
              <span className="italic text-muted-foreground">Tap vào mọi thứ khác.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-balance text-[15px] leading-relaxed text-muted-foreground md:text-base">
              Năm chất liệu. Một nền tảng. Mỗi Banana Card kết hợp phần cứng cao cấp với hồ sơ AI banana.cards, CRM, booking và follow-up — sẵn sàng, không cần app.
            </p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href="#collection" className="inline-flex items-center gap-2 rounded-xl bg-foreground px-6 py-3 text-sm font-bold text-background transition hover:opacity-90">
                Duyệt bộ sưu tập <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/vi/pricing" className="inline-flex items-center gap-2 rounded-xl border border-foreground/15 px-6 py-3 text-sm font-bold text-foreground transition hover:bg-muted">
                Xem gói nền tảng
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5" /> Miễn phí vận chuyển toàn cầu trên $50</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Bảo hành phần cứng trọn đời</span>
              <span className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> 4.9 · 2,100+ đánh giá</span>
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="collection" className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-20">
          <div className="mb-10 flex items-end justify-between gap-6">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Mọi thẻ</p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Toàn bộ dòng Banana Cards</h2>
            </div>
            <p className="hidden max-w-sm text-sm text-muted-foreground md:block">
              Mỗi thẻ ship sẵn sàng tap — kết với hồ sơ banana.cards miễn phí trong 60 giây.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {COPY.map((c, i) => {
              const p = productBySlug(c.slug);
              if (!p) return null;
              const fromPrice = getProductBasePrice(p.slug, country.code);
              return (
                <Link
                  key={c.slug}
                  to={p.href as "/shop/metal"}
                  className="group relative flex flex-col overflow-hidden rounded-3xl bg-card transition hover:-translate-y-0.5"
                >
                  <div className="relative aspect-[5/4] overflow-hidden bg-background">
                    <img
                      src={c.image}
                      alt={p.name}
                      loading={i < 2 ? "eager" : "lazy"}
                      className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.03]"
                    />
                    {c.badge && (
                      <span className={`absolute left-4 top-4 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${c.accent}`}>
                        <c.Icon className="h-3 w-3" /> {c.badge}
                      </span>
                    )}
                    <span className="absolute right-4 top-4 rounded-full bg-background/90 px-3 py-1 text-[11px] font-bold tabular-nums text-foreground backdrop-blur">
                      Từ {formatMoney(fromPrice, country)}
                    </span>
                  </div>

                  <div className="flex flex-1 flex-col p-5 md:p-6">
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{c.tagline}</p>
                    <h3 className="mt-1.5 font-display text-2xl leading-tight md:text-3xl">{p.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>

                    <ul className="mt-4 space-y-1.5">
                      {c.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-[13px] text-foreground/80">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-5 flex items-center justify-between border-t border-foreground/10 pt-4">
                      <span className="text-sm font-semibold text-foreground">Mua {p.shortName}</span>
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-background transition group-hover:translate-x-0.5">
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Cách hoạt động</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Từ mở hộp đến tap đầu tiên trong 60 giây</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { n: "01", t: "Chọn & cá nhân hoá", d: "Chọn chất liệu, upload logo, chúng tôi làm thẻ.", Icon: Sparkles },
              { n: "02", t: "Tap để kích hoạt", d: "Tap 1 lần trên điện thoại để claim hồ sơ banana.cards miễn phí.", Icon: Smartphone },
              { n: "03", t: "Chia sẻ & follow up", d: "Tap để chia sẻ. AI capture mọi contact và viết follow-up.", Icon: Zap },
            ].map((s) => (
              <div key={s.n} className="rounded-3xl bg-muted p-6">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">Bước {s.n}</span>
                  <s.Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <h3 className="mt-3 font-display text-2xl leading-tight">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 flex flex-col items-start justify-between gap-3 md:flex-row md:items-end">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">So sánh chất liệu</p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Banana Card nào phù hợp với bạn?</h2>
            </div>
            <p className="max-w-sm text-sm text-muted-foreground">
              Mọi thẻ chạy cùng nền tảng AI. Khác biệt là ấn tượng đầu tiên.
            </p>
          </div>

          <div className="overflow-x-auto rounded-3xl bg-card">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-foreground/10 text-left text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  <th className="px-5 py-3">Thẻ</th>
                  <th className="px-5 py-3">Phù hợp cho</th>
                  <th className="px-5 py-3">Cảm giác</th>
                  <th className="px-5 py-3">Từ</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {[
                  { slug: "metal",         best: "Founder, sales pro",      feel: "Nặng, cao cấp, cảm giác mát" },
                  { slug: "24kt-gold",     best: "Luxury, hospitality",     feel: "Showpiece, gương" },
                  { slug: "bamboo",        best: "Thương hiệu bền vững",   feel: "Ấm, tự nhiên, nhẹ" },
                  { slug: "white-pvc",     best: "Team & startup",          feel: "Sạch, in màu đầy đủ" },
                  { slug: "google-review", best: "Doanh nghiệp địa phương", feel: "PVC sáng, sẵn quầy" },
                ].map((row) => {
                  const p = productBySlug(row.slug)!;
                  return (
                    <tr key={row.slug} className="border-b border-foreground/10 last:border-0">
                      <td className="px-5 py-4 font-semibold">{p.name}</td>
                      <td className="px-5 py-4 text-muted-foreground">{row.best}</td>
                      <td className="px-5 py-4 text-muted-foreground">{row.feel}</td>
                      <td className="px-5 py-4 tabular-nums">{formatMoney(getProductBasePrice(p.slug, country.code), country)}</td>
                      <td className="px-5 py-4 text-right">
                        <Link to={p.href as "/shop/metal"} className="inline-flex items-center gap-1 text-sm font-bold text-foreground hover:underline">
                          Mua <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PLATFORM STRIP */}
      <section className="relative overflow-hidden bg-brand-dark text-background">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 md:grid-cols-12 md:px-6 md:py-24">
          <div className="md:col-span-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/70">
              Mọi thẻ đều có
            </p>
            <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Một nền tảng AI, không chỉ là thẻ.
            </h2>
            <p className="mt-4 max-w-md text-[15px] leading-relaxed text-background/80">
              Thẻ là trigger. Đằng sau là CRM, engine AI follow-up, smart booking, e-signing hợp đồng và analytics team — bao gồm từ ngày đầu.
            </p>
            <Link to="/vi/pricing" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-brand-lime px-5 py-2.5 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90">
              Xem có gì <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {[
                { t: "Hồ sơ AI",       d: "Đa ngôn ngữ, tự cập nhật",  Icon: Sparkles },
                { t: "Capture liên hệ", d: "Mỗi tap, không mất",       Icon: RefreshCw },
                { t: "Follow-up AI",   d: "Soạn trong vài giây",       Icon: Zap },
                { t: "Booking",         d: "Lịch & video bao gồm",    Icon: Calendar },
                { t: "Analytics",       d: "Theo dõi mọi tương tác",  Icon: BarChart3 },
                { t: "Kiểm soát team",  d: "Mời, brand, quản trị",    Icon: ShieldCheck },
              ].map((f) => (
                <div key={f.t} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <f.Icon className="h-4 w-4 text-brand-lime" />
                  <p className="mt-3 text-sm font-bold">{f.t}</p>
                  <p className="mt-0.5 text-[12px] text-background/70">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROOF */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid gap-6 md:grid-cols-4">
            {[
              { n: "2,100+",  l: "Thẻ ngoài thực địa" },
              { n: "4.9 / 5", l: "Đánh giá khách hàng" },
              { n: "60 giây", l: "Thời gian setup TB" },
              { n: "120+",    l: "Quốc gia đã ship" },
            ].map((s) => (
              <div key={s.l} className="rounded-3xl bg-muted p-6 text-center">
                <p className="font-display text-4xl">{s.n}</p>
                <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted">
        <div className="mx-auto max-w-4xl px-4 py-14 md:px-6 md:py-20">
          <div className="mb-10 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">FAQ</p>
            <h2 className="mt-2 font-display text-3xl md:text-4xl">Câu hỏi về thẻ</h2>
          </div>
          <div className="space-y-3">
            {[
              { q: "Tôi có cần app để chia sẻ thẻ không?", a: "Không. NFC hoạt động native trên mọi iOS và Android trong thập kỷ qua. Tap 1 lần — hồ sơ đầy đủ mở trong browser." },
              { q: "Tôi có thể order 1 thẻ thử rồi nâng cấp sau?", a: "Có. Bắt đầu với 1 thẻ để test, sau đó bundle 3, 5 hoặc 10 với giá giảm khi sẵn sàng triển khai team." },
              { q: "Thẻ có thể re-program không?", a: "Thẻ không bao giờ hết hạn và dữ liệu có thể cập nhật bất cứ lúc nào từ dashboard — không cần in lại." },
              { q: "Ship mất bao lâu?", a: "Thẻ tùy chỉnh ship trong 3–5 ngày làm việc. Miễn phí ship toàn cầu cho đơn trên $50." },
              { q: "Nếu tôi mất thẻ?", a: "Vô hiệu hoá tức thì từ dashboard và đặt thay thế với giá thành viên. Contact và hồ sơ của bạn vẫn an toàn." },
            ].map((f) => (
              <details key={f.q} className="group rounded-2xl bg-card p-5 open:shadow-sm">
                <summary className="flex cursor-pointer items-center justify-between gap-3 text-left text-[15px] font-semibold">
                  {f.q}
                  <span className="ml-4 inline-flex h-7 w-7 items-center justify-center rounded-full border border-foreground/15 text-muted-foreground transition group-open:rotate-45">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24">
          <div className="relative overflow-hidden rounded-3xl bg-foreground p-8 text-background md:p-14">
            <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-brand-lime/40 blur-3xl" />
            <div className="relative grid items-center gap-8 md:grid-cols-12">
              <div className="md:col-span-7">
                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-background/60">Sẵn sàng khi bạn sẵn sàng</p>
                <h2 className="mt-3 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
                  Ấn tượng đầu tiên kế tiếp chỉ cách một tap.
                </h2>
                <p className="mt-4 max-w-md text-[15px] text-background/70">
                  Miễn phí ship trên $50. Bảo hành phần cứng trọn đời. Đổi trả 30 ngày không hỏi.
                </p>
              </div>
              <div className="md:col-span-5">
                <div className="flex flex-wrap gap-3 md:justify-end">
                  <Link to="/shop/metal" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:opacity-90">
                    Mua bestseller <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold text-background transition hover:bg-background/10">
                    Liên hệ sales
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}